import type {ReactElement} from 'react';
import React, { useEffect, useRef} from 'react';
import {Panel, PanelHeader} from '@Components/panel';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {openTrimAudioModalForSelectedAudioItem} from '@Components/poster-editor/library/poster-editor-open-modals';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import type {BottomBarItemProps} from '@Components/contextual-menu/components/bottom-bar-item';
import {openPosterEditorReplaceAudioModal} from '@Modals/poster-editor-replace-audio-modal';
import {useAppSelector} from '@/hooks';
import styles from './edit-audio-item-panel.module.scss';

export interface EditAudioItemPanelProps {
  panelId: string;
  onClose?(): void;
}

enum AudioControls {
  PLAY_PAUSE = 'playPause',
  TRIM = 'trim',
  REPLACE = 'replace',
  DELETE = 'delete',
}

function EditAudioItemPanel({...props}: EditAudioItemPanelProps): ReactElement | null {
  const containerRef = useRef<HTMLDivElement>(null);
  const onClose = useDefaultPanelClose(props.panelId);

  const selectedAudioItemUID = useAppSelector((state) => {
    return state.posterEditor.posterObject?.audioClips.selectedAudioItemUID ?? '';
  });
  const allAudioItems = useAppSelector((state) => {
    return state.posterEditor.posterObject?.audioClips.audioItemsHashMap;
  });
  const isAudioPlaylistSelected = selectedAudioItemUID !== '';

  useEffect(() => {
    if (!isAudioPlaylistSelected) {
      handleClose();
    }
  }, [isAudioPlaylistSelected]);

  const getAudioItemName = (): string => {
    if (allAudioItems) {
      const currentAudio = allAudioItems[selectedAudioItemUID];
      if (currentAudio) {
        return currentAudio.name;
      }
    }
    return '';
  };

  const getHeader = (): ReactElement => {
    return <PanelHeader title={getAudioItemName()} onClose={handleClose} />;
  };

  const handleClose = (): void => {
    onClose();
    if (props.onClose) {
      props.onClose();
    }
  };

  const onClickHandler = (id: AudioControls): void => {
    switch (id) {
      case AudioControls.TRIM:
        openTrimAudioModalForSelectedAudioItem();
        break;
      case AudioControls.DELETE:
        void window.posterEditor?.whiteboard?.audioClips.deleteSelectedAudioItem();
        break;
      case AudioControls.REPLACE:
        openPosterEditorReplaceAudioModal();
        break;
      default:
        break;
    }
  };

  const getItems = (): Array<BottomBarItemProps> => {
    return [
      {
        id: AudioControls.TRIM,
        text: window.i18next.t('pmwjs_trim'),
        icon: 'icon-trim-video',
      },
      {
        id: AudioControls.REPLACE,
        icon: 'icon-replace',
        text: window.i18next.t('pmwjs_replace'),
      },
      {
        id: AudioControls.DELETE,
        icon: 'icon-delete',
        text: window.i18next.t('pmwjs_delete'),
        iconClasses: 'color-danger-100',
      },
    ];
  };

  const getContent = (): ReactElement => {
    return (
      <div className={`spacing-p-l-1 spacing-p-r-1 spacing-p-b-4 spacing-p-t-1 ${styles.editAudioItemPanel}`}>
        <BottomBar isForEditorBottomBar={false} id="audio-item-edit-options" className={styles.bottomBar} onClick={onClickHandler} items={getItems()} />
      </div>
    );
  };

  return <Panel ref={containerRef} panelHeader={getHeader()} panelContent={getContent()} />;
}

export default EditAudioItemPanel;
